import {
  useVbsWorkOrderPreviewPDFQuery,
  useVbsWorkOrderShowQuery,
  useVbsWorkOrderUpdateRepresentativeMutation,
  useVbsWorkOrderUpdateServiceAddressMutation,
  useVbsWorkOrderUpdateServiceContactMutation,
  WorkOrderDocument,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { Button } from "@/shared/components/ui/button";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import {
  DocumentActionsDownload,
  DocumentActionsHeader,
} from "@/shared/bsm/core/document/components/ui/document-immutable-page";
import { DownloadButton } from "@/services/backend/vbs/work-orders/components/work-order-download-buttons";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RefreshCw } from "lucide-react";
import { useVbsLMRAShowQuery } from "@/services/backend/vbs/work-orders/api/lmra-api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  DocumentPage,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentationCard } from "@/services/backend/vbs/work-orders/components/documentation-card";
import { WorkOrderBeginDateInput } from "@/services/backend/vbs/work-orders/components/work-order-begin-date-input";
import { WorkOrderEndDateInput } from "@/services/backend/vbs/work-orders/components/work-order-end-date-input";
import { WorkOrderExecutingBranchSelect } from "@/services/backend/vbs/work-orders/components/work-order-executing-branch-select";
import { WorkOrderTeamLeaderInput } from "@/services/backend/vbs/work-orders/components/work-order-team-leader-input";
import { WorkOrderInternalCommentInput } from "@/services/backend/vbs/work-orders/components/work-order-comment-input";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { ContactCardWithMutation } from "@/services/backend/contacts/components/contact-card";
import { RepresentativeCardWithMutation } from "@/services/backend/representatives/components/representative-card";
import { TreatmentComponent } from "@/services/backend/vbs/treatments/components/treatment-component";
import { Column } from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import { ConfirmedLMRAComponent } from "@/services/backend/vbs/work-orders/components/confirmed-lmra";
import { UnconfirmedLMRAComponent } from "@/services/backend/vbs/work-orders/components/unconfirmed-lmra";
import { PricingCards } from "@/services/backend/vbs/pricing/components/pricing-cards";
import { CalculationComponent } from "@/routes/processes/[processId]/vbs/calculations/[calculationId]/calculation/calculation-route";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function VbsWorkOrderPreview({ workOrderId }: { workOrderId: string }) {
  const {
    data: workOrder,
    isLoading,
    error,
  } = useVbsWorkOrderShowQuery({ id: workOrderId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <WorkOrderDocumentSheet workOrder={workOrder!} />
    </DocumentSheetContextProvider>
  );
}

function WorkOrderDocumentSheet({ workOrder }: { workOrder: WorkOrder }) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(workOrder!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          {isImmutable(workOrder!) ? (
            <ImmutableWorkOrderPreview workOrder={workOrder!} />
          ) : (
            <MutableWorkOrderPreview workOrder={workOrder!} />
          )}
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function ImmutableWorkOrderPreview({ workOrder }: { workOrder: WorkOrder }) {
  const { data, isLoading, isFetching, error } = useVbsWorkOrderPreviewPDFQuery(
    {
      id: workOrder.id,
      document: WorkOrderDocument.All,
    },
    // Rendered inside the sheet's content, so it mounts when the sheet
    // opens: refetch then, showing the last PDF until the fresh one arrives.
    { refetchOnMountOrArgChange: true },
  );

  return (
    <>
      <DocumentSheetPreview
        isLoading={isLoading}
        isFetching={isFetching}
        objectURL={data?.objectURL}
        error={error}
      />
      <ImmutableWorkOrderActions workOrder={workOrder} />
    </>
  );
}

function ImmutableWorkOrderActions({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <DocumentSheetActions>
      <DocumentActionsHeader
        title={t("Angebot")}
        documentNumber={
          <ReferenceNumber referenceNumber={workOrder.referenceNumber} />
        }
      />
      <DocumentSheetActionsContent>
        <DocumentActionsDownload>
          <DownloadButton
            workOrderId={workOrder.id}
            document={WorkOrderDocument.All}
            text={t("Alle Dokumente")}
          />
          <DownloadButton
            workOrderId={workOrder.id}
            document={WorkOrderDocument.WorkOrder}
            text={t("Arbeitsschein")}
          />
          <DownloadButton
            workOrderId={workOrder.id}
            document={WorkOrderDocument.Documentation}
            text={t("Dokumentation")}
          />
          <DownloadButton
            workOrderId={workOrder.id}
            document={WorkOrderDocument.LastMinuteRiskAnalysis}
            text={t("Gefährdungsbeurteilung")}
          />
        </DocumentActionsDownload>
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}

function MutableWorkOrderPreview({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <>
      <MutableWorkOrderView workOrder={workOrder} />
      <MutableWorkOrderActions workOrder={workOrder} />
    </>
  );
}

function MutableWorkOrderView({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <Tabs defaultValue="data">
            <DocumentPageHeader>
              <TabsList>
                <TabsTrigger value="data">{t("Daten")}</TabsTrigger>
                <TabsTrigger value="pricing">{t("Preis")}</TabsTrigger>
                <TabsTrigger value="calculation">
                  {t("Kalkulation")}
                </TabsTrigger>
                <TabsTrigger value="treatment">{t("Behandlung")}</TabsTrigger>

                <TabsTrigger value="lmra">{t("LMRA")}</TabsTrigger>
                <TabsTrigger value="documentation">
                  {t("Dokumentation")}
                </TabsTrigger>
              </TabsList>
              <span className="ml-4 font-mono text-lg text-gray-900">
                <ReferenceNumber referenceNumber={workOrder.referenceNumber} />
              </span>
              <PersistenceStateUsingContext className="mr-2 ml-auto" />
            </DocumentPageHeader>
            <DocumentPageContent className="max-h-[75vh] overflow-auto">
              <TabsContent value="data">
                <BaseData workOrder={workOrder} />
              </TabsContent>
              <TabsContent value="pricing">
                <PricingCards pricingId={workOrder.pricingId} />;
              </TabsContent>
              <TabsContent value="calculation">
                <CalculationComponent calculationId={workOrder.calculationId} />
              </TabsContent>
              <TabsContent value="treatment">
                <TreatmentComponent
                  treatmentId={workOrder.treatmentId}
                  defaultColumns={[
                    Column.Position,
                    Column.Completed,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.ActuallyTreated,
                    Column.Comment,
                    Column.Actions,
                  ]}
                />
              </TabsContent>
              <TabsContent value="lmra">
                <LMRALoader lmraId={workOrder.lastMinuteRiskAnalysisId} />
              </TabsContent>
              <TabsContent value="documentation">
                <DocumentationCard
                  documentationId={workOrder.documentationId}
                  workOrderId={workOrder.id}
                />
              </TabsContent>
            </DocumentPageContent>
          </Tabs>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function BaseData({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <div className="grid gap-2">
      <PreviewBaseData workOrder={workOrder!} />
      <ServiceAddressCardWithMutation
        id={workOrder!.id}
        address={workOrder!.serviceAddress}
        mutation={useVbsWorkOrderUpdateServiceAddressMutation}
        disabled
      />
      <div className="md:grid md:grid-cols-2 md:gap-2">
        <ContactCardWithMutation
          id={workOrder!.id}
          contact={workOrder!.serviceContact}
          fieldName="serviceContact"
          mutation={useVbsWorkOrderUpdateServiceContactMutation}
          disabled
        />
        <RepresentativeCardWithMutation
          id={workOrder!.id}
          representative={workOrder!.representative}
          mutation={useVbsWorkOrderUpdateRepresentativeMutation}
          disabled
        />
      </div>
    </div>
  );
}

function PreviewBaseData({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Arbeitsschein")}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <WorkOrderBeginDateInput workOrder={workOrder} disabled />
          <WorkOrderEndDateInput workOrder={workOrder} disabled />
          <WorkOrderExecutingBranchSelect workOrder={workOrder} disabled />
          <WorkOrderTeamLeaderInput workOrder={workOrder} disabled />
        </div>
        <WorkOrderInternalCommentInput workOrder={workOrder} />
      </CardContent>
    </Card>
  );
}

function LMRALoader({ lmraId }: { lmraId: string }) {
  const {
    data: lmra,
    isLoading,
    error,
  } = useVbsLMRAShowQuery({
    id: lmraId!,
  });

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (!lmra) return null;

  if (lmra.confirmed) {
    return <ConfirmedLMRAComponent lmra={lmra} />;
  }
  return <UnconfirmedLMRAComponent lmra={lmra} />;
}

function MutableWorkOrderActions({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(workOrder.documentKind)}
        documentNumber={
          <ReferenceNumber referenceNumber={workOrder.referenceNumber} />
        }
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={workOrder.id}
          documentKind={workOrder.documentKind}
          processId={workOrder.processId}
          serviceKind={GSMServiceKind.VBS}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
